import * as go from 'gojs';
import { SYMBOLS } from './gojs-schematic.symbols';

export function withoutUndo(diagram: go.Diagram, fn: () => void): void {
  const prev = diagram.skipsUndoManager;
  diagram.skipsUndoManager = true;
  try {
    fn();
  } finally {
    diagram.skipsUndoManager = prev;
  }
}

export function busbarFraction(busbar: go.Node, docPt: go.Point): number {
  const picture = busbar.findObject('BAR_PICTURE') ?? busbar;
  const localPt = picture.getLocalPoint(docPt);
  const length = (busbar.data?.length as number) || SYMBOLS['BB'].w;
  return Math.min(1, Math.max(0, localPt.x / length));
}

export function isPortLinked(node: go.Node, pid: string): boolean {
  return node.findLinksConnected().any(
    (lnk: go.Link) =>
      (lnk.fromNode === node && (lnk.data as any)?.fromPort === pid) ||
      (lnk.toNode   === node && (lnk.data as any)?.toPort   === pid)
  );
}

export function isWithinLinkLimits(
  fromNode: go.Node | null,
  toNode: go.Node | null,
  excludeLink?: go.Link
): boolean {
  if (fromNode && toNode) {
    const busOrJunction = ['BB', 'JN'];
    if (busOrJunction.includes(fromNode.data?.category) && busOrJunction.includes(toNode.data?.category)) return false;
    const alreadyConnected = fromNode.findLinksBetween(toNode).any(
      (lnk: go.Link) => lnk !== excludeLink
    );
    if (alreadyConnected) return false;
  }
  if (fromNode) {
    const sym = SYMBOLS[fromNode.data?.category as string];
    if (sym?.maxSources !== undefined) {
      let count = 0;
      fromNode.findLinksConnected().each((lnk: go.Link) => {
        if (lnk !== excludeLink && lnk.fromNode === fromNode) count++;
      });
      if (count >= sym.maxSources) return false;
    }
  }
  if (toNode) {
    if (toNode.data?.sourceAcLineId != null) return false;

    const sym = SYMBOLS[toNode.data?.category as string];
    if (sym?.maxDestinations !== undefined) {
      let count = 0;
      toNode.findLinksConnected().each((lnk: go.Link) => {
        if (lnk !== excludeLink && lnk.toNode === toNode) count++;
      });
      if (count >= sym.maxDestinations) return false;
    }
  }
  return true;
}

export function cleanupAllPorts(diagram: go.Diagram): void {
  withoutUndo(diagram, () => {
    diagram.nodes.each((node: go.Node) => {
      if (['LABEL', 'BB', 'LN'].includes(node.data?.category)) return;
      ['T', 'R', 'B', 'L'].forEach(pid => {
        const p = node.findPort(pid);
        if (p && p !== node && !isPortLinked(node, pid)) p.opacity = 0;
      });
    });
  });
}

export class PortShowingLinkingTool extends go.LinkingTool {
  private static setPorts(node: go.Node, show: boolean): void {
    ['T', 'R', 'B', 'L'].forEach(pid => {
      const p = node.findPort(pid);
      if (!p || p === node) return;
      p.opacity = show ? 1 : (isPortLinked(node, pid) ? 1 : 0);
    });
  }

  override isValidLink(
    fromNode: go.Node, fromPort: go.GraphObject,
    toNode: go.Node, toPort: go.GraphObject
  ): boolean {
    return super.isValidLink(fromNode, fromPort, toNode, toPort) &&
      isWithinLinkLimits(fromNode, toNode);
  }

  override canStart(): boolean {
    if (!super.canStart()) return false;
    const node = this.findLinkablePort()?.part;
    return !(node instanceof go.Node && node.data?.category === 'BB');
  }

  override doMouseMove(): void {
    super.doMouseMove();
    const part = this.diagram.findPartAt(this.diagram.lastInput.documentPoint);
    const hover = (part instanceof go.Node && part.data?.category !== 'LABEL') ? part : null;
    withoutUndo(this.diagram, () => {
      this.diagram.nodes.each((node: go.Node) => {
        if (['LABEL', 'BB', 'LN'].includes(node.data?.category)) return;
        PortShowingLinkingTool.setPorts(node, node === hover || node === this.originalFromNode);
      });
    });
  }

  override doDeactivate(): void {
    super.doDeactivate();
    withoutUndo(this.diagram, () => {
      this.diagram.nodes.each((node: go.Node) => {
        if (['LABEL', 'BB', 'LN'].includes(node.data?.category)) return;
        PortShowingLinkingTool.setPorts(node, false);
      });
    });
  }
}

export class SyncLabelDraggingTool extends go.DraggingTool {
  private _lblOrigLocs = new Map<string, go.Point>();

  override doActivate(): void {
    super.doActivate();

    const startPart = this.diagram.findPartAt(this.diagram.firstInput.documentPoint);
    if (startPart instanceof go.Node && startPart.data?.category === 'LABEL' && this.draggedParts) {
      const master = this.diagram.findNodeForKey(startPart.data.master);
      if (master) this.draggedParts.remove(master);
    }

    this._lblOrigLocs.clear();
    this.draggedParts?.each((entry: go.IKeyValuePair<go.Part, go.DraggingInfo>) => {
      const node = entry.key;
      if (!(node instanceof go.Node) || !node.data?.labelKey) return;
      const lbl = this.diagram.findNodeForKey(node.data.labelKey);
      if (lbl) this._lblOrigLocs.set(node.data.labelKey as string, lbl.location.copy());
    });
  }

  override moveParts(parts: go.Map<go.Part, go.DraggingInfo>, offset: go.Point, check: boolean): void {
    super.moveParts(parts, offset, check);
    parts.each((entry: go.IKeyValuePair<go.Part, go.DraggingInfo>) => {
      const node = entry.key;
      if (!(node instanceof go.Node) || !node.data?.labelKey) return;
      const lbl = this.diagram.findNodeForKey(node.data.labelKey);
      if (!lbl || parts.has(lbl)) return;
      const orig = this._lblOrigLocs.get(node.data.labelKey as string);
      if (!orig) return;
      this.diagram.model.setDataProperty(lbl.data, 'loc',
        go.Point.stringify(orig.copy().offset(offset.x, offset.y)));
    });
  }

  override doDeactivate(): void {
    super.doDeactivate();
    cleanupAllPorts(this.diagram);
  }
}

export class BusbarRightClickLinkingTool extends go.LinkingTool {
  pendingFromFrac = 0.5;

  override canStart(): boolean {
    // This fully replaces LinkingTool.canStart() rather than calling super,
    // so the diagram.allowLink gate has to be re-checked here explicitly —
    // otherwise a locked schematic could still have busbar links drawn via
    // right-click.
    if (!this.diagram.allowLink) return false;
    if (!this.diagram.lastInput.right) return false;
    const part = this.diagram.findPartAt(this.diagram.lastInput.documentPoint);
    if (!(part instanceof go.Node) || part.data?.category !== 'BB') return false;

    this.pendingFromFrac = busbarFraction(part, this.diagram.lastInput.documentPoint);
    this.isForwards = true;
    this.originalFromNode = part;
    this.originalFromPort = part.port;
    return true;
  }

  override isValidLink(
    fromNode: go.Node, fromPort: go.GraphObject,
    toNode: go.Node, toPort: go.GraphObject
  ): boolean {
    return isWithinLinkLimits(fromNode, toNode);
  }

  override doMouseMove(): void {
    super.doMouseMove();
    const part = this.diagram.findPartAt(this.diagram.lastInput.documentPoint);
    const hover = (part instanceof go.Node && !['LABEL', 'BB'].includes(part.data?.category)) ? part : null;
    withoutUndo(this.diagram, () => {
      this.diagram.nodes.each((node: go.Node) => {
        if (['LABEL', 'BB', 'LN'].includes(node.data?.category)) return;
        ['T', 'R', 'B', 'L'].forEach(pid => {
          const p = node.findPort(pid);
          if (!p || p === node) return;
          p.opacity = (node === hover || isPortLinked(node, pid)) ? 1 : 0;
        });
      });
    });
  }

  override doDeactivate(): void {
    super.doDeactivate();
    cleanupAllPorts(this.diagram);
  }
}

export class BusbarAwareRelinkingTool extends go.RelinkingTool {
  override isValidLink(
    fromNode: go.Node, fromPort: go.GraphObject,
    toNode: go.Node, toPort: go.GraphObject
  ): boolean {
    const exclude = this.originalLink ?? undefined;
    if (fromNode?.data?.category === 'BB' || toNode?.data?.category === 'BB') {
      if (fromNode === null || toNode === null) return false;
      return isWithinLinkLimits(fromNode, toNode, exclude);
    }
    if (!super.isValidLink(fromNode, fromPort, toNode, toPort)) return false;
    return isWithinLinkLimits(fromNode, toNode, exclude);
  }
}
